import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { useNativeInputSync } from "../hooks/use-native-input-sync"
import { Textarea } from "../components/ui/textarea"
import { stripPresetConflicts } from "../lib/preset-utils"

export function TextareaRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("textarea")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const placeholder = element.placeholder ? String(resolveSymbol(element.placeholder) ?? "") : undefined
  const textDefault = element.text ? String(resolveSymbol(element.text) ?? "") : ""

  const [localValue, setLocalValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    textDefault,
    String,
  )

  // Native DOM input event listener — catches Playwright page.fill()/page.type()
  const nativeRef = useNativeInputSync((value) => {
    setLocalValue(value)
    dispatchEvent(element.id, "change", { value })
  })

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value)
    dispatchEvent(element.id, "change", { value: e.target.value })
  }

  return (
    <Textarea
      ref={nativeRef}
      className={stripPresetConflicts(element.className)}
      placeholder={placeholder}
      {...(element.bind !== undefined ? { value: localValue } : { defaultValue: textDefault })}
      onChange={handleChange}
      data-testid={`textarea-${element.id}`}
      data-action="input"
      data-atom-type="textarea"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}
